// src/pages/QuizFlowPage.js
// Streamlined quiz flow — no auth required, 20 questions, 4 options, auto-advance.

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { dilemmaQuestions } from '../data/dilemmaQuestions';
import {
  calculateDilemmaScores,
  determineClusterFromScores,
} from '../utils/dilemmaScoring';
import { computeTensions } from '../utils/tensionMapper';
import { getTypeByCluster } from '../data/typeDefinitions';
import { useAuth } from '../contexts/AuthContext';
import { createLogger } from '../utils/logger';
import { trackEvent } from '../utils/analytics';

const logger = createLogger('QuizFlow');
const TOTAL_QUESTIONS = dilemmaQuestions.length;

function QuizFlowPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const compareId = searchParams.get('compare');
  const predictId = searchParams.get('predict');
  const sourceParam = searchParams.get('source'); // 'friend' | 'couple' | 'group' | etc.
  const trackedStart = useRef(false);
  const { currentUser } = useAuth();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);
  const [direction, setDirection] = useState(1);
  const [predictTarget, setPredictTarget] = useState(null);

  // Track quiz start and invite opened
  useEffect(() => {
    if (trackedStart.current) return;
    trackedStart.current = true;
    // Priority: predict_invite > compare_invite > explicit ?source= from a
    // landing-page CTA > 'direct'. Store the ?source= value even when the
    // primary source is an invite, so acquisition attribution isn't lost.
    const source = predictId
      ? 'predict_invite'
      : compareId
        ? 'compare_invite'
        : sourceParam || 'direct';
    trackEvent('quiz_started', {
      source,
      landingSource: sourceParam || undefined,
    });
    if (compareId) trackEvent('invite_opened', { compareId });
    if (predictId) trackEvent('invite_opened', { predictId, mode: 'predict' });
  }, [compareId, predictId, sourceParam]);

  // Fire a beacon if the user leaves the quiz before completing it.
  // Refs track live state so the effect (mounted once) can read the latest
  // values without re-subscribing. completedRef is set right before the
  // navigate to /results so we don't double-count as abandon + complete.
  const completedRef = useRef(false);
  const stateRef = useRef({ index: currentIndex, answered: 0 });
  useEffect(() => {
    stateRef.current = {
      index: currentIndex,
      answered: Object.keys(answers).length,
    };
  }, [currentIndex, answers]);
  useEffect(() => {
    const onLeave = () => {
      if (completedRef.current) return;
      const { index, answered } = stateRef.current;
      // Only fire if the user actually started answering
      if (index === 0 && answered === 0) return;
      trackEvent('quiz_abandoned', {
        atQuestion: index + 1,
        total: TOTAL_QUESTIONS,
        answered,
      });
    };
    window.addEventListener('pagehide', onLeave);
    return () => {
      window.removeEventListener('pagehide', onLeave);
      onLeave();
    };
  }, []);

  // If predict mode, fetch the original quiz-taker's answers
  useEffect(() => {
    if (!predictId) return;
    (async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase/config');
        const snap = await getDoc(doc(db, 'comparisons', predictId));
        if (snap.exists()) {
          setPredictTarget(snap.data());
        }
      } catch (err) {
        logger.warn('Failed to load predict target:', err);
      }
    })();
  }, [predictId]);

  const currentQuestion = dilemmaQuestions[currentIndex];
  const progress = ((currentIndex + 1) / TOTAL_QUESTIONS) * 100;

  const handleSelect = useCallback(
    optionKey => {
      if (selectedOption) return; // prevent double-click
      setSelectedOption(optionKey);

      const newAnswers = { ...answers, [currentQuestion.id]: optionKey };
      setAnswers(newAnswers);

      // Per-question analytics — lets us find which question kills the funnel.
      trackEvent('question_answered', {
        qid: currentQuestion.id,
        qIndex: currentIndex + 1,
        total: TOTAL_QUESTIONS,
        option: optionKey,
      });

      // Auto-advance after brief delay
      setTimeout(() => {
        if (currentIndex < TOTAL_QUESTIONS - 1) {
          setDirection(1);
          setCurrentIndex(prev => prev + 1);
          setSelectedOption(null);
        } else {
          // Quiz complete — compute results
          const scores = calculateDilemmaScores(newAnswers);
          const tensions = computeTensions(scores);
          const { name: clusterName } = determineClusterFromScores(scores);
          const typeDef = getTypeByCluster(clusterName);

          const results = {
            scores,
            tensions,
            clusterName,
            typeName: typeDef.typeName,
            answers: newAnswers,
            completedAt: new Date().toISOString(),
          };

          localStorage.setItem('wwyd_results', JSON.stringify(results));

          completedRef.current = true;
          trackEvent('quiz_completed', {
            type: typeDef.typeName,
            cluster: clusterName,
          });

          // Mirror to Firestore for logged-in users so cross-device access
          // and the retention email hook have something to query. Fire and
          // forget — never block the results reveal on a network write.
          if (currentUser) {
            (async () => {
              try {
                const { doc, setDoc, serverTimestamp } = await import(
                  'firebase/firestore'
                );
                const { db } = await import('../firebase/config');
                await setDoc(
                  doc(db, 'users', currentUser.uid),
                  {
                    email: currentUser.email || null,
                    quizResults: {
                      typeName: typeDef.typeName,
                      clusterName,
                      completedAt: results.completedAt,
                    },
                    oneLiner: typeDef.oneLiner,
                    lastQuizDate: serverTimestamp(),
                  },
                  { merge: true }
                );
              } catch (err) {
                logger.warn('Failed to mirror quiz results to Firestore:', err);
              }
            })();
          }

          // Predict mode — score predictions vs. actual answers, navigate to predict result
          if (predictId && predictTarget?.answers) {
            const actual = predictTarget.answers;
            let correct = 0;
            const breakdown = [];
            for (const q of dilemmaQuestions) {
              const guess = newAnswers[q.id];
              const real = actual[q.id];
              const right = guess === real;
              if (right) correct++;
              breakdown.push({ qid: q.id, guess, real, right });
            }
            const predictResults = {
              correct,
              total: dilemmaQuestions.length,
              breakdown,
              targetType: predictTarget.typeName,
              targetCluster: predictTarget.clusterName,
              predictId,
              completedAt: new Date().toISOString(),
            };
            localStorage.setItem(
              'wwyd_predict_result',
              JSON.stringify(predictResults)
            );
            trackEvent('predict_completed', {
              correct,
              total: dilemmaQuestions.length,
            });
            navigate('/predict-result');
            return;
          }

          // If this was a comparison quiz, write results back and redirect
          if (compareId) {
            localStorage.setItem(
              'wwyd_compare_results',
              JSON.stringify(results)
            );

            // Write invitee results back to Firestore so inviter can view
            (async () => {
              try {
                const { doc, updateDoc } = await import('firebase/firestore');
                const { db } = await import('../firebase/config');
                const compRef = doc(db, 'comparisons', compareId);
                await updateDoc(compRef, {
                  partnerResults: {
                    scores: results.scores,
                    tensions: results.tensions,
                    clusterName: results.clusterName,
                    typeName: results.typeName,
                    completedAt: results.completedAt,
                  },
                });
              } catch (err) {
                logger.warn(
                  'Failed to write partner results to Firestore:',
                  err
                );
              }
            })();

            navigate(`/compare/${compareId}`);
          } else {
            navigate('/results');
          }
        }
      }, 400);
    },
    [
      answers,
      currentIndex,
      currentQuestion,
      selectedOption,
      navigate,
      compareId,
      predictId,
      predictTarget,
      currentUser,
    ]
  );

  const handleBack = useCallback(() => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex(prev => prev - 1);
      setSelectedOption(null);
    }
  }, [currentIndex]);

  const slideVariants = {
    enter: dir => ({ x: dir > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: dir => ({ x: dir > 0 ? -300 : 300, opacity: 0 }),
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Editorial progress rail — typographic chapter counter,
          hairline underline, no fat colored bar. */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-neutral-200">
        <div className="max-w-2xl mx-auto px-6 py-3.5">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="text-sm font-medium text-neutral-500 hover:text-neutral-900 disabled:opacity-0 transition-opacity"
            >
              ← Back
            </button>
            <span className="font-display text-sm text-neutral-500 tabular-nums tracking-wide">
              <span className="text-neutral-900 font-semibold">
                {String(currentIndex + 1).padStart(2, '0')}
              </span>
              <span className="mx-1 text-neutral-300">/</span>
              <span>{String(TOTAL_QUESTIONS).padStart(2, '0')}</span>
            </span>
            <span aria-hidden className="w-14" />
          </div>
          {/* Very thin progress hairline underneath — accent gold */}
          <div className="mt-2 w-full h-px bg-neutral-200">
            <motion.div
              className="h-px bg-neutral-900"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            />
          </div>
        </div>
      </div>

      {/* Question area */}
      <div className="flex-1 flex items-center justify-center px-6 pt-32 pb-16">
        <div className="w-full max-w-2xl">
          {predictId && (
            <div className="mb-6 text-center">
              <p className="eyebrow">Predicting how your friend answered</p>
            </div>
          )}
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentQuestion.id}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            >
              {/* Chapter number above the question — typographic anchor */}
              <p className="font-display text-lg text-neutral-300 mb-3 text-center tabular-nums">
                {String(currentIndex + 1).padStart(2, '0')}
              </p>
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-neutral-900 mb-10 leading-tight tracking-tight text-center">
                {currentQuestion.text}
              </h2>

              {/* Options — restrained rows, hairline dividers, no colored buttons */}
              <div className="divide-y divide-neutral-200 border-y border-neutral-200">
                {currentQuestion.options.map(option => {
                  const isSelected = selectedOption === option.key;
                  const isPreviousAnswer =
                    !selectedOption &&
                    answers[currentQuestion.id] === option.key;
                  const dimmed = selectedOption && !isSelected;

                  return (
                    <button
                      key={option.key}
                      onClick={() => handleSelect(option.key)}
                      disabled={!!selectedOption}
                      className={`group w-full text-left px-5 py-5 transition-all duration-150 ${
                        isSelected
                          ? 'bg-neutral-900 text-white'
                          : isPreviousAnswer
                            ? 'bg-neutral-50 text-neutral-900'
                            : 'bg-white hover:bg-neutral-50 text-neutral-800'
                      } ${dimmed ? 'opacity-40' : ''}`}
                    >
                      <span className="text-base md:text-lg leading-snug block">
                        {option.text}
                      </span>
                    </button>
                  );
                })}
              </div>

              <p className="mt-6 text-center text-xs text-neutral-400">
                Answer with your gut. There are no right answers.
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default QuizFlowPage;
